import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {Observable} from 'rxjs';
import {XeroConfiguration, XeroConfigurationRequest} from '../models/xero/xeroConfiguration.model';
import {XeroSyncLog} from '../models/xero/xeroSyncLog.model';
import {XeroAccount} from '../models/xero/xeroAccount.model';
import {XeroTaxRate} from '../models/xero/xeroTaxRate.model';
import {XeroTenant} from '../models/xero/xeroTenant.model';

@Injectable({
  providedIn: 'root'
})
export class XeroService {
  private XERO_URL = environment.BASE_URL + 'xero';

  constructor(private http: HttpClient) {}

  getConfiguration(): Observable<XeroConfiguration> {
    return this.http.get<XeroConfiguration>(`${this.XERO_URL}/configuration`);
  }

  createConfiguration(request: XeroConfigurationRequest): Observable<XeroConfiguration> {
    return this.http.post<XeroConfiguration>(`${this.XERO_URL}/configuration`, request);
  }

  updateConfiguration(id: string, request: XeroConfigurationRequest): Observable<XeroConfiguration> {
    return this.http.patch<XeroConfiguration>(`${this.XERO_URL}/configuration/${id}`, request);
  }

  getAuthUrl(): Observable<{ url: string }> {
    return this.http.get<{ url: string }>(`${this.XERO_URL}/auth/url`);
  }

  handleCallback(code: string, state: string): Observable<XeroConfiguration> {
    return this.http.post<XeroConfiguration>(`${this.XERO_URL}/auth/callback`, {code, state});
  }

  getAvailableTenants(): Observable<XeroTenant[]> {
    return this.http.get<XeroTenant[]>(`${this.XERO_URL}/tenants`);
  }

  selectTenant(configId: string, tenantId: string): Observable<XeroConfiguration> {
    return this.http.post<XeroConfiguration>(`${this.XERO_URL}/configuration/${configId}/tenant`, {tenantId});
  }

  getXeroAccounts(): Observable<XeroAccount[]> {
    return this.http.get<XeroAccount[]>(`${this.XERO_URL}/accounts`);
  }

  getXeroTaxRates(): Observable<XeroTaxRate[]> {
    return this.http.get<XeroTaxRate[]>(`${this.XERO_URL}/tax-rates`);
  }

  syncContact(accountId: string): Observable<XeroSyncLog> {
    return this.http.post<XeroSyncLog>(`${this.XERO_URL}/sync/contact`, {accountId});
  }

  syncQuote(jobId: string): Observable<XeroSyncLog> {
    return this.http.post<XeroSyncLog>(`${this.XERO_URL}/sync/quote`, {jobId});
  }

  syncInvoice(jobId: string): Observable<XeroSyncLog> {
    return this.http.post<XeroSyncLog>(`${this.XERO_URL}/sync/invoice`, {jobId});
  }

  syncBills(jobId: string): Observable<XeroSyncLog[]> {
    return this.http.post<XeroSyncLog[]>(`${this.XERO_URL}/sync/bills`, {jobId});
  }

  syncCreditNote(creditNoteId: string): Observable<XeroSyncLog> {
    return this.http.post<XeroSyncLog>(`${this.XERO_URL}/sync/credit-note`, {creditNoteId});
  }

  getSyncLogs(): Observable<XeroSyncLog[]> {
    return this.http.get<XeroSyncLog[]>(`${this.XERO_URL}/sync/logs`);
  }

  retryFailedSyncs(): Observable<void> {
    return this.http.post<void>(`${this.XERO_URL}/sync/retry`, {});
  }
}
